import {HiMinus, HiOutlineTrash, HiPlus} from 'react-icons/hi2'
import {useDebounceCallback} from 'usehooks-ts'
import {useTranslations} from 'use-intl'
import {memo, useState} from 'react'
import clsx from 'clsx'
import {BasketItemWithImage, getProperty} from '~/utils/basket'
import Price from '~/components/common/Price'

type BasketItemListProps = {
    className?: string
    basketItems: BasketItemWithImage[]
    onQuantityChange: (props: {
        productId: string
        sizeId: string
        appearanceId: string
        quantity: number
    }) => void
}

export const BasketItemList = ({className, basketItems, onQuantityChange}: BasketItemListProps) => {
    const [deletedItems, setDeletedItems] = useState<BasketItemWithImage[]>([])

    return (
        <ul className={clsx('divide-y divide-dashed divide-gray-600', className)}>
            {basketItems.map((item) => {
                const productId = getProperty(item.element.properties, 'productLookupId')!
                const sizeId = getProperty(item.element.properties, 'size')!
                const appearanceId = getProperty(item.element.properties, 'appearance')!
                return (
                    <BasketItem
                        key={`${productId}-${appearanceId}-${sizeId}`}
                        item={item}
                        onQuantityChange={(quantity) =>
                            onQuantityChange({productId, sizeId, appearanceId, quantity})
                        }
                        onDelete={() => {
                            setDeletedItems((prev) => [...prev, item])
                            onQuantityChange({productId, sizeId, appearanceId, quantity: 0})
                        }}
                    />
                )
            })}
        </ul>
    )
}
type BasketItemProps = {
    item: BasketItemWithImage
    onQuantityChange: (quantity: number) => void
    onDelete: () => void
}

const BasketItem = memo(({item, onQuantityChange, onDelete}: BasketItemProps) => {
    const t = useTranslations('basket.item')
    const [quantity, setQuantity] = useState<number>(item.quantity)

    const appearanceLabel = getProperty(item.element.properties, 'appearanceLabel')
    const sizeLabel = getProperty(item.element.properties, 'sizeLabel')

    const onDebouncedQuantityChange = useDebounceCallback(onQuantityChange, 500)

    return (
        <li
            className="pl-2 md:pl-4 lg:pl-6 pr-4 md:pr-6 lg:pr-8 mt-4 md:mt-6 lg:mt-8 first:mt-0 last:mb-4 last:md:mb-6 last:lg:mb-8 grid grid-cols-3 gap-4 pt-4 md:pt-6 lg:pt-8"
            key={item.id}
        >
            <img src={item.image.src} alt={item.description} />
            <div className="col-span-2 space-y-1.5">
                <div className="space-y-0.5">
                    <div className="overflow-hidden truncate font-medium">{item.description}</div>
                    <div className="text-xs">
                        <span className="capitalize">{appearanceLabel}</span>,{' '}
                        <span>{sizeLabel}</span>
                    </div>
                </div>
                <div className="flex justify-end items-baseline gap-1">
                    <Price className="font-medium">{quantity * item.price.display}</Price>
                </div>
                <div className="flex gap-6 items-center justify-between">
                    <button
                        type="button"
                        className="size-8 flex justify-center items-center p-1.5"
                        onClick={onDelete}
                    >
                        <HiOutlineTrash aria-hidden="true" className="size-full" />
                        <span className="sr-only">{t('remove')}</span>
                    </button>
                    <div className="flex items-center flex-row">
                        <button
                            type="button"
                            className="border rounded-l-md size-8 flex justify-center items-center disabled:opacity-30"
                            disabled={quantity <= 1}
                            onClick={() => {
                                setQuantity((prevState) => {
                                    const newState = Math.max(1, prevState - 1)
                                    onDebouncedQuantityChange(newState)
                                    return newState
                                })
                            }}
                        >
                            <HiMinus aria-hidden="true" />
                            <span className="sr-only">{t('quantity.decrease')}</span>
                        </button>
                        <input
                            aria-label={t('quantity.label')}
                            className="text-white w-full h-8 z-10 max-w-12 bg-transparent text-center rounded-none block border -mx-px outline-none focus:ring-inset focus:ring-offset-0 focus:ring-1 focus:ring-nova focus:border-nova"
                            type="text"
                            pattern="\d*"
                            min={1}
                            step={1}
                            value={quantity}
                            onChange={(event) => {
                                const inputQuantity = Number(event.currentTarget.value)

                                if (!isNaN(inputQuantity) && inputQuantity >= 0) {
                                    setQuantity(inputQuantity)

                                    if (inputQuantity > 0) {
                                        onDebouncedQuantityChange(inputQuantity)
                                    }
                                }
                            }}
                            onBlur={(event) => {
                                const inputQuantity = Number(event.currentTarget.value) ?? quantity
                                const newQuantity = inputQuantity >= 0 ? inputQuantity : quantity
                                setQuantity(newQuantity)
                                onQuantityChange(newQuantity)
                            }}
                        />
                        <button
                            type="button"
                            className="border rounded-r-md size-8 flex justify-center items-center"
                            onClick={() => {
                                setQuantity((prevState) => {
                                    onDebouncedQuantityChange(prevState + 1)
                                    return prevState + 1
                                })
                            }}
                        >
                            <HiPlus aria-hidden="true" />
                            <span className="sr-only">{t('quantity.increase')}</span>
                        </button>
                    </div>
                </div>
            </div>
        </li>
    )
})
